import api from "./axios";

/**
 * Get current user's profile data
 * @returns {Promise} Profile data
 */
export const getMyProfile = async () => {
  const response = await api.get("/profile/me");
  return response.data;
};

/**
 * Update current user's profile data
 * @param {Object} profileData - Profile data to update
 * @returns {Promise} Updated profile data
 */
export const updateMyProfile = async (profileData) => {
  const response = await api.put("/profile/me", profileData);
  return response.data;
};

/**
 * What stands in the way of deleting the signed-in account, and how it's confirmed
 * (`confirmWith`: "password", or "username" for an account without one).
 */
export const getAccountDeletionCheck = async () => {
  const response = await api.get("/profile/me/deletion-check");
  return response.data;
};

/**
 * Delete the signed-in account for good.
 * @param {{ password?: string, username?: string }} confirmation
 */
export const deleteMyAccount = async (confirmation) => {
  const response = await api.delete("/profile/me", { data: confirmation });
  return response.data;
};

/**
 * Get user profile by ID
 * @param {string} userId - User ID
 * @returns {Promise} API response
 */
export const getUserProfile = async (userId) => {
  const response = await api.get(`/profile/${userId}`);
  return response.data;
};

/**
 * Generate Instagram verification code
 * @param {string} username - Instagram username
 * @returns {Promise} API response
 */
export const generateInstagramCode = async (username) => {
  const response = await api.post("/profile/instagram/generate-code", { username });
  return response.data;
};

/**
 * Verify Instagram account
 * @returns {Promise} API response
 */
export const verifyInstagram = async () => {
  const response = await api.post("/profile/instagram/verify");
  return response.data;
};

/**
 * Normalize GET /profile/:id (own or other) or GET /profile/me into one user-shaped object.
 */
export function profileUserFromApiResponse(response, fallbackUserId) {
  if (!response?.success) return null;
  if (response.data) {
    const d = response.data;
    return { ...d, id: d._id || d.id || fallbackUserId };
  }
  if (response.profile) {
    return {
      _id: fallbackUserId,
      id: fallbackUserId,
      username: response.user?.username,
      profile: response.profile,
      instagramVerification: response.instagramVerification,
    };
  }
  return null;
}

/** Merge PUT /profile/me auth payload with saved profile fields for AuthContext. */
export function mergeAuthUserAfterProfileSave(prev, saveResponse, socialsOverride) {
  if (!saveResponse?.success) return prev;
  const next = saveResponse.user ? { ...(prev || {}), ...saveResponse.user } : { ...(prev || {}) };
  const savedProfile = saveResponse.profile || {};
  const socials =
    socialsOverride ||
    savedProfile.socials ||
    saveResponse.user?.profile?.socials ||
    prev?.profile?.socials;
  next.profile = {
    ...(prev?.profile || {}),
    ...(saveResponse.user?.profile || {}),
    ...savedProfile,
    ...(socials ? { socials } : {}),
  };
  if (saveResponse.user?.instagramVerification) {
    next.instagramVerification = saveResponse.user.instagramVerification;
  }
  return next;
}

/** Followers and following counts, and whether the viewer and this person follow each other. */
export const getFollowStats = async (userId) => {
  const response = await api.get(`/profile/${userId}/follows`);
  return response.data;
};

export const followUser = async (userId) => {
  const response = await api.post(`/profile/${userId}/follow`);
  return response.data;
};

export const unfollowUser = async (userId) => {
  const response = await api.delete(`/profile/${userId}/follow`);
  return response.data;
};

/**
 * One page of a person's Followers or Following list.
 * @param {"followers"|"following"} list
 */
export const getFollowList = async (userId, list, page = 1) => {
  const response = await api.get(`/profile/${userId}/${list}`, { params: { page } });
  return response.data;
};
